import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "../components/CartContext";
import { Heart, Share2, Truck, RefreshCw, ShieldCheck, Star } from "lucide-react";
import data from "../Data.json";
import getSymbolFromCurrency from 'currency-symbol-map';
import { useCurrency } from "../components/CurrencyContext";
import toast, { Toaster } from "react-hot-toast";

const ProductView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [mainImage, setMainImage] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [isWishlisted, setIsWishlisted] = useState(false);
  const { addToCart } = useCart();
  const { rate, currency } = useCurrency();

  const convertPrice = (price) => {
    const productPrice = parseFloat(price) || 0;
    const convertedPrice = productPrice * rate;
    return convertedPrice
  }
  const currencySymbol = currency === "SAR" ? "﷼" : getSymbolFromCurrency(currency) || currency;


  const apiProducts = JSON.parse(localStorage.getItem('apiProducts') || '[]');

  // Find the product from API data first, if not found use JSON data
  const product = apiProducts.find(p => p.product_id.toString() === id) ||
    data.products.find((p, index) => index.toString() === id);

  console.log(
    product
  );


  // Get related products from the same source as the current product
  const relatedProducts = (() => {
    // If current product is from API
    if (apiProducts.find(p => p.product_id.toString() === id)) {
      // Get other API products (exclude current product)
      return apiProducts
        .filter(p => p.product_id.toString() !== id)
        .slice(0, 4);
    }
    // If current product is from JSON data
    else {
      // Get products from the same category if possible
      const currentCategory = product?.category;
      const sameCategory = currentCategory
        ? data.products.filter((p, index) => p.category === currentCategory && index.toString() !== id)
        : [];

      // If there are enough products in the same category, use them
      if (sameCategory.length >= 4) {
        return sameCategory.slice(0, 4);
      }
      // Otherwise, use other products from data.json
      else {
        return data.products
          .filter((p, index) => index.toString() !== id)
          .slice(0, 4);
      }
    }
  })();

  useEffect(() => {
    if (product) {
      // Use product image from JSON data if API product doesn't have image
      const productImage = product.image ||
        data.products.find(p => p.id === product.product_id)?.image ||
        data.products[0].image;

      setMainImage(productImage);

      if (product.colors?.length > 0) {
        setSelectedColor(product.colors[0]);
      }
      if (product.sizes?.length > 0) {
        setSelectedSize(product.sizes[0]);
      }
    }

  }, [product]);

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast.error("Please select a size.");
      return;
    }

    addToCart(product, selectedSize, selectedColor, quantity);
    navigate("/checkout", {
      state: {
        cart: [{ ...product, selectedSize, selectedColor, price: product.price, quantity }],
      },
    });
  };

  const handleQuantityChange = (change) => {
    setQuantity(Math.max(1, quantity + change));
  };

  const toggleWishlist = () => {
    setIsWishlisted(!isWishlisted);
    // Add logic to save to wishlist in a real application
  };

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Product Not Found</h1>
          <p className="text-gray-600 mb-6">The product you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-black hover:bg-gray-800 text-white rounded-lg transition-all duration-300 transform hover:scale-105"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  const renderStars = (rating) => {
    const stars = [];
    const fullRating = Math.floor(rating || 4.5);
    const hasHalfStar = (rating || 4.5) - fullRating >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullRating) {
        stars.push(<Star key={i} className="fill-yellow-400 text-yellow-400" size={18} />);
      } else if (i === fullRating && hasHalfStar) {
        stars.push(<Star key={i} className="fill-yellow-400 text-yellow-400" size={18} />);
      } else {
        stars.push(<Star key={i} className="text-gray-300" size={18} />);
      }
    }
    return stars;
  };

  return (
    <div className="bg-white pt-20">
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4 px-6 mt-16">
        <div className="container mx-auto">
          <nav className="flex text-sm">
            <a href="/" className="text-gray-500 hover:text-black">Home</a>
            <span className="mx-2 text-gray-400">/</span>
            <a href="/category" className="text-gray-500 hover:text-black">{product.category || "Category"}</a>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-800 font-medium">{product.product_name}</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row -mx-4">
          {/* Product Images Section */}
          <div className="lg:w-7/12 px-4 mb-10 lg:mb-0">
            <div className="flex flex-col-reverse md:flex-row">
              {/* Thumbnails */}
              <div className="flex md:flex-col mt-4 md:mt-0 md:mr-6 overflow-x-auto md:overflow-y-auto md:h-[500px] scrollbar-thin scrollbar-thumb-gray-300">
                {product.images?.map((img, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 mr-3 md:mr-0 md:mb-3"
                  >
                    <img
                      src={`${process.env.REACT_APP_API_URI}/uploads/${img}`}
                      alt={`Thumbnail ${index + 1}`}
                      onClick={() => setMainImage(img)}
                      className={`w-20 h-20 md:w-24 md:h-24 object-cover rounded-lg cursor-pointer transition-all duration-300 hover:shadow-md ${mainImage === img ? "ring-2 ring-black" : "opacity-70 hover:opacity-100"
                        }`}
                    />
                  </div>
                ))}
              </div>

              {/* Main Image */}
              <div className="flex-grow relative group">
                <div className="w-full h-[500px] bg-gray-50 rounded-xl overflow-hidden">
                  <img
                    src={`${process.env.REACT_APP_API_URI}/uploads/${mainImage}`}
                    alt={product.product_name}
                    className="w-full h-full object-contain p-4 transform transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute top-4 right-4 flex space-x-2">
                  <button
                    onClick={toggleWishlist}
                    className={`p-2 rounded-full ${isWishlisted ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-600'} hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <Heart className={isWishlisted ? 'fill-red-500' : ''} size={18} />
                  </button>
                  <button className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors duration-300">
                    <Share2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Product Details Section */}
          <div className="lg:w-5/12 px-4">
            <div className="sticky top-24">
              {/* Brand and Name */}
              <div className="mb-6">
                <span className="text-sm text-gray-500 uppercase tracking-wider">{product.brand || "Premium Brand"}</span>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mt-1">{product.product_name}</h1>

                {/* Ratings */}
                <div className="flex items-center mt-3">
                  <div className="flex mr-2">
                    {renderStars(product.rating)}
                  </div>
                  <span className="text-sm text-gray-500">
                    {product.rating || "4.5"} ({product.reviewCount || "42"} reviews)
                  </span>
                </div>
              </div>

              {/* Price */}
              <div className="mb-8">
                <div className="flex items-center">
                  <span className="text-3xl font-bold text-gray-900">{currencySymbol} {convertPrice(product?.price).toFixed(2)}</span>
                  {product.originalPrice && (
                    <span className="ml-3 text-xl text-gray-400 line-through">${product.originalPrice}</span>
                  )}
                  {product.discountPercentage && (
                    <span className="ml-3 px-2 py-1 bg-red-100 text-red-700 text-sm font-medium rounded">
                      {product.discountPercentage}% OFF
                    </span>
                  )}
                </div>
                <p className="text-green-600 text-sm mt-2">
                  {product.availability || "In Stock"} · Free shipping on orders over $50
                </p>
              </div>

              {/* Product options */}
              <div className="space-y-6 border-t border-gray-200 pt-6">





                {/* Units (Milliliters) */}
                {product.units?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-900">
                        Milliliter: <span className="font-normal ml-1">{selectedSize}</span>
                      </h3>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {product.units.map((unit) => (
                        <button
                          key={unit.product_unit_id}
                          onClick={() => setSelectedSize(unit.unit_value)}
                          className={`flex items-center justify-center border rounded py-2 text-sm font-medium ${selectedSize === unit.unit_value
                            ? "bg-black text-white border-black"
                            : "bg-white text-gray-900 border-gray-200 hover:bg-gray-50"
                            }`}
                        >
                          {unit.unit_value}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Colors */}
                {/* {product.colors?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Color: <span className="font-normal capitalize ml-1">{selectedColor}</span></h3>
                    <div className="flex flex-wrap gap-2">
                      {product.colors.map((color) => (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(color)}
                          className={`relative w-10 h-10 rounded-full transition-transform ${selectedColor === color ? "transform scale-110 ring-2 ring-black ring-offset-2" : ""
                            }`}
                          style={{ backgroundColor: color }}
                          title={color}
                        ></button>
                      ))}
                    </div>
                  </div>
                )} */}


                {/* <h3>Milliliter</h3> 
                <p>{product.units.map((unit)=>{
                  return <span key={unit} className="inline-block bg-gray-200 text-gray-800 text-xs px-2 py-1 rounded-full mr-2">{unit.unit_value}</span>
                })}</p> */}

                {/* Sizes */}
                {/* {product.sizes?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-900">Size: <span className="font-normal ml-1">{selectedSize}</span></h3>
                      <button className="text-sm font-medium text-black hover:text-gray-700 underline">Size guide</button>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {product.sizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`flex items-center justify-center border rounded py-2 text-sm font-medium ${selectedSize === size
                              ? "bg-black text-white border-black"
                              : "bg-white text-gray-900 border-gray-200 hover:bg-gray-50"
                            }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )} */}

                {/* Quantity */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Quantity</h3>
                  <div className="flex items-center border border-gray-200 rounded-lg w-36">
                    <button
                      className="px-3 py-2 text-gray-500 hover:text-black focus:outline-none"
                      onClick={() => handleQuantityChange(-1)}
                    >
                      -
                    </button>
                    <input
                      type="text"
                      value={quantity}
                      readOnly
                      className="w-12 text-center border-0 focus:outline-none text-gray-900"
                    />
                    <button
                      className="px-3 py-2 text-gray-500 hover:text-black focus:outline-none"
                      onClick={() => handleQuantityChange(1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Add to cart and Buy now buttons */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 mt-8">
                <button
                  onClick={handleAddToCart}
                  className="flex-1 px-6 py-3 bg-black hover:bg-gray-800 text-white font-medium rounded-lg transition-all duration-300"
                >
                  Add to Cart
                </button>
                <button
                  onClick={handleAddToCart}
                  className="flex-1 px-6 py-3 bg-white border border-black text-black font-medium rounded-lg hover:bg-gray-50 transition-all duration-300"
                >
                  Buy Now
                </button>
              </div>

              {/* Benefits/Features */}
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Truck size={18} className="text-gray-700" />
                  <span className="text-sm text-gray-600">Free Shipping</span>
                </div>
                <div className="flex items-center space-x-2">
                  <RefreshCw size={18} className="text-gray-700" />
                  <span className="text-sm text-gray-600">30-Day Returns</span>
                </div>
                <div className="flex items-center space-x-2">
                  <ShieldCheck size={18} className="text-gray-700" />
                  <span className="text-sm text-gray-600">Secure Checkout</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star size={18} className="text-gray-700" />
                  <span className="text-sm text-gray-600">Premium Quality</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Information Tabs */}
        <div className="mt-16 border-t border-gray-200">
          <div className="flex border-b border-gray-200 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab("description")}
              className={`px-6 py-4 text-sm font-medium whitespace-nowrap ${activeTab === "description"
                ? "border-b-2 border-black text-black"
                : "text-gray-500 hover:text-black"
                }`}
            >
              Product Description
            </button>
            <button
              onClick={() => setActiveTab("details")}
              className={`px-6 py-4 text-sm font-medium whitespace-nowrap ${activeTab === "details"
                ? "border-b-2 border-black text-black"
                : "text-gray-500 hover:text-black"
                }`}
            >
              Additional Information
            </button>
            <button
              onClick={() => setActiveTab("reviews")}
              className={`px-6 py-4 text-sm font-medium whitespace-nowrap ${activeTab === "reviews"
                ? "border-b-2 border-black text-black"
                : "text-gray-500 hover:text-black"
                }`}
            >
              Reviews ({product.reviewCount || "42"})
            </button>
            <button
              onClick={() => setActiveTab("shipping")}
              className={`px-6 py-4 text-sm font-medium whitespace-nowrap ${activeTab === "shipping"
                ? "border-b-2 border-black text-black"
                : "text-gray-500 hover:text-black"
                }`}
            >
              Shipping & Returns
            </button>
          </div>

          <div className="py-8">
            {activeTab === "description" && (
              <div className="prose max-w-none">
                <p className="text-gray-600 leading-relaxed">
                  {product.description ||
                    `This premium product features exceptional quality and craftsmanship. Designed with attention to detail, 
                   it combines style, functionality, and durability to meet the needs of discerning customers. 
                   Made from high-quality materials that ensure longevity and performance.`}
                </p>
                <div className="mt-6 grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-medium text-lg mb-3">Key Features</h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-600">
                      <li>Premium quality materials</li>
                      <li>Ergonomic design for comfort</li>
                      <li>Versatile for multiple occasions</li>
                      <li>Durable construction for long-lasting use</li>
                      <li>Easy to maintain and clean</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-medium text-lg mb-3">Perfect For</h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-600">
                      <li>Everyday use</li>
                      <li>Special occasions</li>
                      <li>Gift-giving</li>
                      <li>Professional settings</li>
                      <li>Personal collection</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "details" && (
              <div className="space-y-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="py-3 text-sm font-medium text-gray-900 w-1/4">Material</td>
                      <td className="py-3 text-sm text-gray-600">{product.material || "Premium materials"}</td>
                    </tr>
                    <tr>
                      <td className="py-3 text-sm font-medium text-gray-900">Dimensions</td>
                      <td className="py-3 text-sm text-gray-600">{product.dimensions || "See size guide"}</td>
                    </tr>
                    <tr>
                      <td className="py-3 text-sm font-medium text-gray-900">Weight</td>
                      <td className="py-3 text-sm text-gray-600">{product.weight || "Lightweight"}</td>
                    </tr>
                    <tr>
                      <td className="py-3 text-sm font-medium text-gray-900">Care Instructions</td>
                      <td className="py-3 text-sm text-gray-600">{product.care || "See product tag for details"}</td>
                    </tr>
                    <tr>
                      <td className="py-3 text-sm font-medium text-gray-900">Warranty</td>
                      <td className="py-3 text-sm text-gray-600">{product.warranty || "1 year limited warranty"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "reviews" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium">Customer Reviews</h3>
                  <button className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors">
                    Write a Review
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Sample reviews */}
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="border-b border-gray-200 pb-6 last:border-0">
                      <div className="flex justify-between mb-2">
                        <div>
                          <h4 className="font-medium">Customer Name</h4>
                          <div className="flex mt-1">
                            {renderStars(5)}
                          </div>
                        </div>
                        <span className="text-sm text-gray-500">3 months ago</span>
                      </div>
                      <p className="mt-3 text-gray-600">
                        This product exceeded my expectations. The quality is exceptional and it looks even better in person.
                        Highly recommended for anyone looking for a premium product.
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "shipping" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-3">Shipping Information</h3>
                  <p className="text-gray-600">
                    {product.delivery ||
                      `We ship worldwide with several shipping options to choose from. Standard shipping typically takes 3-5 business days for domestic orders. 
                     International shipping may take 7-14 business days depending on the destination.
                     All orders are processed within 24 hours during business days. Free shipping is available for orders over $50.`}
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium mb-3">Return Policy</h3>
                  <p className="text-gray-600">
                    {product.returns ||
                      `We offer a 30-day return policy for all unused and undamaged items in their original packaging. 
                     Return shipping costs may apply depending on the reason for return. 
                     Please contact our customer service team to initiate a return or exchange.`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products Section */}
        <div className="mt-16 border-t border-gray-200 pt-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">You May Also Like</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {relatedProducts.map((item, index) => {
              // Determine the ID based on source (API or JSON)
              const productId = item.product_id !== undefined ? item.product_id : index;

              // Use image from the item or fall back to a default
              const productImage = item.image ||
                (product.image ? product.image : data.products[0]?.image);

              // Use product_name from API product or name from JSON product
              const productName = item.product_name || item.name || `Product ${productId}`;

              return (
                <div
                  key={productId}
                  className="group cursor-pointer"
                  onClick={() => navigate(`/product/${productId}`)}
                >
                  <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-50 mb-4 group-hover:opacity-75 transition-opacity">
                    <img
                      src={`${process.env.REACT_APP_API_URI}/uploads/${productImage}`}
                      alt={productName}
                      className="h-80 w-full object-cover object-center"
                    />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900">
                      {productName}
                    </h3>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-lg font-medium text-gray-900">
                        {currencySymbol} {convertPrice(item?.price).toFixed(2)}
                        {item.originalPrice && (
                          <span className="ml-2 text-sm line-through text-gray-500">
                            ${item.originalPrice}
                          </span>
                        )}
                      </p>
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                        <span className="ml-1 text-sm text-gray-500">{item.rating || "4.5"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Toaster
        position="top-right"  
        reverseOrder={false}
      />
    </div>
  );
};

export default ProductView;